// hooks/user/useAdminUsersList.js
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook for fetching users list for admin management
 *
 * Provides search, filtering, and pagination for admin user management.
 * Requirements: 11.1, 11.2, 11.3
 *
 * @param {Object} options - Query options
 * @param {string} options.search - Search term for username/email
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.limit - Items per page
 * @param {string} options.sortBy - Sort field (username, createdAt, verified)
 * @param {string} options.sortOrder - Sort order (asc, desc)
 * @param {boolean} options.verifiedFilter - Filter by verification status (null for all)
 * @returns {Object} Query result with users data and pagination info
 */
export const useAdminUsersList = ({
  search = "",
  page = 1,
  limit = 20,
  sortBy = "createdAt",
  sortOrder = "desc",
  verifiedFilter = null,
} = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = process.env.REACT_APP_API_URL;

  return useQuery(
    [
      "adminUsersList",
      { search, page, limit, sortBy, sortOrder, verifiedFilter },
    ],
    async () => {
      const token = await getAccessTokenSilently();

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });

      if (search.trim()) {
        params.append("search", search.trim());
      }

      if (verifiedFilter !== null) {
        params.append("verified", verifiedFilter.toString());
      }

      const response = await axios.get(
        `${apiUrl}/api/admin/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout for admin queries
        }
      );

      // Extract the data from the success wrapper
      return response.data.data;
    },
    {
      enabled: !!getAccessTokenSilently,
      staleTime: 2 * 60 * 1000, // 2 minutes - admin data should be relatively fresh
      cacheTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry authorization errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
    }
  );
};
